import React, { useState, useEffect, useRef } from 'react';
import { TEMPERAMENT_SYSTEMS, NOTE_OPTIONS_12, JI_SEMITONE_RATIOS, SOUND_OPTIONS, DRONE_SOUND_OPTIONS } from '../data/musicData.js';
import { getSampler, playSampleWithTemperament, getAvailableNotesForInstrument, getOctaveRangeForInstrument, markSamplerInUse, markSamplerNotInUse } from '../utils/samplerManager.js';
import * as Tone from 'tone';

const PRESET_METERS = [
  { name: "1/4", numerator: 1, denominator: 4 },
  { name: "2/4", numerator: 2, denominator: 4 },
  { name: "3/4", numerator: 3, denominator: 4 },
  { name: "4/4", numerator: 4, denominator: 4 },
  { name: "5/4", numerator: 5, denominator: 4 },
  { name: "5/8", numerator: 5, denominator: 8 },
  { name: "6/8", numerator: 6, denominator: 8 },
  { name: "7/8", numerator: 7, denominator: 8 },
  { name: "9/8", numerator: 9, denominator: 8 },
  { name: "12/8", numerator: 12, denominator: 8 },
];

const SUBDIVISION_OPTIONS = [
  { name: "None", value: 1 },
  { name: "2 (8th notes)", value: 2 },
  { name: "3 (Triplets)", value: 3 },
  { name: "4 (16th notes)", value: 4 },
];

// Filter DRONE_SOUND_OPTIONS to only include tanpura (remove piano, casio, flute, guitar)
const SIMPLE_METRONOME_DRONE_SOUNDS = DRONE_SOUND_OPTIONS.map(category => {
  if (category.category === "Instruments") {
    return {
      ...category,
      sounds: category.sounds.filter(sound => 
        !['piano', 'casio', 'guitar', 'flute'].includes(sound.id)
      )
    };
  }
  return category;
});

export default function SimpleMetronome({
  isPlaying,
  onStartStop,
  tempo,
  setTempo,
  currentBeat,
  measures,
  updateMeasure,
  setMeasures,
  selectedNoteValue,
  setSelectedNoteValue,
  NOTE_VALUE_OPTIONS,
  audioContextRef,
  scheduledBeatsRef
}) {
  const createNewBeat = () => {
    return {
      id: "clicker",
      name: "Clicker",
      type: "clicker",
      color: "#4A90E2",
      userVolume: 1.0,
      beatVolume: 1.0,
      noPitchAccent: false,
      subdivisions: [
        {
          subCount: 1,
          accentedSubs: [],
          mutedSubs: [],
        }
      ],
      dronePitchNote: null,
      dronePitchOctave: 4,
      droneVolume: 1.0,
      hasHarmony: false,
      selectedHarmony: "Major",
      justIntonation: false,
      temperamentSystem: "Equal Temperament",
      jiReferencePitch: 0,
      copyDroneHarmonyPartialsToNext: false,
      usePartials: false,
      selectedPartials: [],
      useCustomNotes: false,
      selectedCustomNotes: [],
      useQuarterTones: false,
      selectedQuarterTones: [],
      quarterToneOctave: 4,
    };
  };

  const [selectedMeter, setSelectedMeter] = useState({ numerator: 4, denominator: 4 });
  const [subdivision, setSubdivision] = useState(1);
  const [currentPlayingBeat, setCurrentPlayingBeat] = useState(null);
  const [currentPlayingSub, setCurrentPlayingSub] = useState(null);
  const rafRef = useRef(null);
  
  // Function to update all beats with new subdivision
  const updateAllBeatsSubdivision = (newSubdivision) => {
    if (!measures[0]) return;
    
    const currentMeasure = measures[0];
    const updatedBeatSounds = currentMeasure.beatSounds.map(beat => ({
      ...beat,
      subdivisions: [
        {
          subCount: newSubdivision,
          accentedSubs: newSubdivision > 1 ? [1] : [], // Accent first subdivision
          mutedSubs: [],
        }
      ]
    }));

    updateMeasure(0, {
      beatSounds: updatedBeatSounds
    });
  };

  // Metronome click sound state
  const [metronomeVolume, setMetronomeVolume] = useState(0.7);
  const [downbeatSound, setDownbeatSound] = useState(SOUND_OPTIONS.find(s => s.id === 'clicker') || SOUND_OPTIONS[0]); 
  const [beatSound, setBeatSound] = useState(SOUND_OPTIONS.find(s => s.id === 'clicker') || SOUND_OPTIONS[0]);
  const lastBeatRef = useRef(null);

  // Function to update all beats with selected sounds
  const updateAllBeatsWithSounds = () => {
    if (!measures[0]) return;
    
    const currentMeasure = measures[0];
    const updatedBeatSounds = currentMeasure.beatSounds.map((beat, index) => {
      const isDownbeat = index === 0;
      const selectedSound = isDownbeat ? downbeatSound : beatSound;
      
      return {
        ...beat,
        ...selectedSound,
        userVolume: metronomeVolume,
        beatVolume: metronomeVolume,
      };
    });

    updateMeasure(0, {
      beatSounds: updatedBeatSounds
    });
  };

  // Update sounds when selections change
  useEffect(() => {
    if (!isPlaying) {
      updateAllBeatsWithSounds();
    }
  }, [downbeatSound, beatSound, metronomeVolume]);

  // Update sounds when meter changes (handled by existing meter change logic)
  // The meter change logic already creates new beats, so we need to apply sounds after that
  useEffect(() => {
    if (measures[0] && !isPlaying) {
      // Small delay to ensure measure update is complete
      setTimeout(() => {
        updateAllBeatsWithSounds();
      }, 10);
    }
  }, [measures[0]?.numerator, measures[0]?.denominator]);

  // Enhanced beat tracking for visual feedback only
  useEffect(() => {
    if (!isPlaying || !audioContextRef.current) {
      setCurrentPlayingBeat(null);
      setCurrentPlayingSub(null);
      lastBeatRef.current = null;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      return;
    }

    function loop() {
      const nowSec = audioContextRef.current.currentTime;
      const schedArr = scheduledBeatsRef.current;

      if (Array.isArray(schedArr) && schedArr.length > 0) {
        let currentBeat = null;
        
        for (let i = schedArr.length - 1; i >= 0; i--) {
          const beat = schedArr[i];
          if (beat.startTimeSec <= nowSec && beat.endTimeSec >= nowSec) {
            currentBeat = beat;
            break;
          }
          if (nowSec - beat.startTimeSec > 1) break;
        }

        if (currentBeat) {
          setCurrentPlayingBeat(currentBeat.beatIndex);
          
          // Calculate which subdivision is currently playing
          if (subdivision > 1) {
            const beatProgress = (nowSec - currentBeat.startTimeSec) / (currentBeat.endTimeSec - currentBeat.startTimeSec);
            const currentSub = Math.floor(beatProgress * subdivision);
            setCurrentPlayingSub(Math.min(currentSub, subdivision - 1));
          } else {
            setCurrentPlayingSub(null);
          }
        } else {
          const lastBeat = schedArr[schedArr.length - 1];
          if (lastBeat && nowSec - lastBeat.endTimeSec < 0.5) {
            setCurrentPlayingBeat(lastBeat.beatIndex);
            setCurrentPlayingSub(null);
          }
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isPlaying, audioContextRef, scheduledBeatsRef, subdivision]);

  // Large visual beat display component
  const BeatDisplay = () => {
    const currentMeasure = measures[0] || { numerator: 4 };
    const numBeats = currentMeasure.numerator;
    const [showBeat, setShowBeat] = useState(false);
    const [currentSub, setCurrentSub] = useState(null);
    
    // Effect to handle beat visualization timing
    useEffect(() => {
      let animationFrame = null;

      if (!isPlaying) {
        setShowBeat(false);
        setCurrentSub(null);
        return;
      }

      function loop() {
        if (!audioContextRef.current) {
          setShowBeat(false);
          setCurrentSub(null);
          return;
        }

        try {
          const nowSec = audioContextRef.current.currentTime;
          const schedArr = scheduledBeatsRef.current;

          if (Array.isArray(schedArr) && schedArr.length > 0) {
            let currentBeat = null;
            
            for (let i = schedArr.length - 1; i >= 0; i--) {
              const beat = schedArr[i];
              if (beat.startTimeSec <= nowSec && beat.endTimeSec >= nowSec) {
                currentBeat = beat;
                break;
              }
              if (nowSec - beat.startTimeSec > 1) break;
            }

            if (currentBeat) {
              const beatProgress = (nowSec - currentBeat.startTimeSec) / (currentBeat.endTimeSec - currentBeat.startTimeSec);
              
              if (numBeats === 1) {
                // For single beat, only show main beat at the start
                setShowBeat(beatProgress <= 0.25);
                // But always show subdivision progress
                setCurrentSub(Math.floor(beatProgress * subdivision));
              } else {
                setShowBeat(true);
                setCurrentSub(currentPlayingSub);
              }
            } else {
              setShowBeat(false);
              setCurrentSub(null);
            }
          } else {
            setShowBeat(false);
            setCurrentSub(null);
          }

          if (isPlaying && audioContextRef.current) {
            animationFrame = requestAnimationFrame(loop);
          }
        } catch (error) {
          setShowBeat(false);
          setCurrentSub(null);
        }
      }

      animationFrame = requestAnimationFrame(loop);
      
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
        setShowBeat(false);
        setCurrentSub(null);
      };
    }, [isPlaying, audioContextRef, scheduledBeatsRef, numBeats, subdivision]);

    return (
      <div className="flex items-center justify-center mb-8 w-full">
        <div className="bg-gray-100 rounded-xl p-4 sm:p-8 w-full max-w-4xl">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {Array.from({ length: numBeats }).map((_, i) => {
              const isActive = numBeats === 1 
                ? (showBeat && isPlaying) 
                : (i === currentPlayingBeat && isPlaying);
              const isDownbeat = i === 0;
              
              return (
                <div key={i} className="flex flex-col items-center">
                  {/* Main beat circle */}
                  <div
                    className={`
                      w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold
                      transition-[transform] duration-75 border-4 mb-2
                      ${isActive 
                        ? (isDownbeat 
                            ? 'bg-accent-600 text-white scale-125 border-accent-600 shadow-lg' 
                            : 'bg-brand-500 text-white scale-125 border-brand-500 shadow-lg'
                          )
                        : (isDownbeat
                            ? 'bg-white border-accent-600 text-accent-600 hover:bg-red-50'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                          )
                      }
                    `}
                  >
                    {i + 1}
                  </div>
                  
                  {/* Subdivision indicators */}
                  {subdivision > 1 && (
                    <div className="flex gap-0.5 sm:gap-1">
                      {Array.from({ length: subdivision }).map((_, subIndex) => (
                        <div
                          key={subIndex}
                          className={`
                            w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-100 mt-2 sm:mt-4
                            ${(numBeats === 1 ? currentSub === subIndex : (isActive && currentPlayingSub === subIndex))
                              ? 'bg-brand-500 scale-125'
                              : subIndex === 0
                                ? 'bg-gray-400' // First subdivision is slightly more prominent
                                : 'bg-gray-300'
                            }
                          `}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Add this useEffect in SimpleMetronome
  useEffect(() => {
    // Initialize the first measure with default subdivisions when component mounts
    if (measures[0] && !isPlaying) {
      updateAllBeatsSubdivision(subdivision);
    }
  }, []); // Run once on mount

  // Also add this to sync subdivision changes
  useEffect(() => {
    if (!isPlaying) {
      updateAllBeatsSubdivision(subdivision);
    }
  }, [subdivision]); // Run when subdivision changes

  // Sustained notes state
   // Sustained notes state
   const [sustainedNotes, setSustainedNotes] = useState([]);
   const [temperamentSystem, setTemperamentSystem] = useState("Equal Temperament");
   const [referenceNote, setReferenceNote] = useState(0); // C
   const [sustainedVolume, setSustainedVolume] = useState(0.3);
   const [selectedDroneSound, setSelectedDroneSound] = useState(SIMPLE_METRONOME_DRONE_SOUNDS[0].sounds[0]);
   const sustainedOscillatorsRef = useRef([]);
   const sustainedSamplerRef = useRef(null);
   const [samplerLoaded, setSamplerLoaded] = useState(false);
   
   // New refs for managing looping samples
   const loopingIntervalsRef = useRef(new Map()); // Map of midiNote -> interval ID
   const activeSampleInstancesRef = useRef(new Map()); // Map of midiNote -> array of active instances

   // Dedicated AudioContext for the sustained-note drone. The metronome
   // transport creates and closes its own context on Start/Stop, so the drone
   // needs an independent context to sound on key-press regardless of play state.
   const droneCtxRef = useRef(null);
   // Tracks the active sound so the effect can tell a notes change (diff) from a
   // sound/sampler change (full rebuild).
   const lastDroneSigRef = useRef('');
   const ensureDroneContext = () => {
     if (!droneCtxRef.current || droneCtxRef.current.state === 'closed') {
       droneCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
     }
     if (droneCtxRef.current.state === 'suspended') {
       droneCtxRef.current.resume().catch(() => {});
     }
     return droneCtxRef.current;
   };
 
   // Helper function to convert MIDI note to Tone.js note name
   const midiToNoteName = (midiNote) => {
     const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
     const octave = Math.floor(midiNote / 12) - 1;
     const noteIndex = midiNote % 12;
     return noteNames[noteIndex] + octave;
   };
 
   // Load sampler when instrument sound is selected
   useEffect(() => {
     const loadSampler = async () => {
       // First, stop all sustained notes to prevent race conditions
       stopSustainedNotes();
       
       // Mark old sampler as not in use
       if (sustainedSamplerRef.current && selectedDroneSound.type === "sampler") {
         markSamplerNotInUse(selectedDroneSound.instrument, 'sustainedNotes');
       }
       
       if (selectedDroneSound.type === "sampler") {
         setSamplerLoaded(false);
         try {
           // Ensure Tone.js context is started
           if (Tone.context.state !== 'running') {
             await Tone.start();
           }
           
           const sampler = await getSampler(selectedDroneSound.instrument);
           sustainedSamplerRef.current = sampler;
           
           // Mark this sampler as in use
           markSamplerInUse(selectedDroneSound.instrument, 'sustainedNotes');
           
           setSamplerLoaded(true);
           console.log(`✅ ${selectedDroneSound.instrument} sampler loaded for sustained notes`);
           
         } catch (error) {
           console.error(`❌ Failed to load ${selectedDroneSound.instrument} sampler:`, error);
           setSamplerLoaded(false);
         }
       } else {
         // Clear sampler for oscillator sounds
         sustainedSamplerRef.current = null;
         setSamplerLoaded(false);
       }
     };
 
     loadSampler();
   }, [selectedDroneSound]);
 
   // Enhanced function to create looping sampler instances
   const createLoopingSamplerNote = (midiNote) => {
     if (!sustainedSamplerRef.current || !samplerLoaded) return null;
 
     const noteName = midiToNoteName(midiNote);
     
     console.log(`🎵 MIDI ${midiNote} → Note: ${noteName}`);
     
     // Calculate temperament adjustment
     const equalTempFreq = 440 * Math.pow(2, (midiNote - 69) / 12);
     const temperamentFreq = getFrequencyForNote(midiNote);
     const detuneCents = 1200 * Math.log2(temperamentFreq / equalTempFreq);
     
     // Apply detune to the sampler
     if (Math.abs(detuneCents) > 0.1) {
       sustainedSamplerRef.current.set({ detune: detuneCents });
     }
     
     // Set volume
     sustainedSamplerRef.current.volume.value = Tone.gainToDb(sustainedVolume * 0.45);
     
     // Estimate sample duration based on instrument
     const estimatedDuration = selectedDroneSound.instrument === 'tanpura' ? 10 : 5;
     const overlapTime = 0.5;
     const loopInterval = (estimatedDuration - overlapTime) * 1000;
     
     const startLooping = () => {
       const triggerInstance = () => {
         if (!sustainedSamplerRef.current || !samplerLoaded || !sustainedNotes.includes(midiNote)) {
           return;
         }
         
         try {
           const instanceId = `${midiNote}_${Date.now()}_${Math.random()}`;
           console.log(`🚀 Triggering: ${noteName}`);
           sustainedSamplerRef.current.triggerAttack(noteName, Tone.now());
           console.log(`✅ Success`);
           
           // Track this instance
           if (!activeSampleInstancesRef.current.has(midiNote)) {
             activeSampleInstancesRef.current.set(midiNote, []);
           }
           activeSampleInstancesRef.current.get(midiNote).push({
             id: instanceId,
             startTime: Tone.now(),
             noteName
           });
           
           // Schedule cleanup
           setTimeout(() => {
             const instances = activeSampleInstancesRef.current.get(midiNote);
             if (instances) {
               const index = instances.findIndex(inst => inst.id === instanceId);
               if (index >= 0) {
                 instances.splice(index, 1);
               }
             }
           }, estimatedDuration * 1000);
           
         } catch (error) {
           console.error(`💥 FAILED: ${noteName} - ${error.message}`);
           
           // Clear interval on error
           const intervalId = loopingIntervalsRef.current.get(midiNote);
           if (intervalId) {
             clearInterval(intervalId);
             loopingIntervalsRef.current.delete(midiNote);
           }
         }
       };
       
       triggerInstance();
       const intervalId = setInterval(triggerInstance, loopInterval);
       loopingIntervalsRef.current.set(midiNote, intervalId);
       
       return intervalId;
     };
     
     return { startLooping, noteName, midiNote };
   };
 
   // Enhanced audio generation function that supports different sound types including looping samples
   const createSustainedAudioNode = async (frequency, audioContext, midiNote) => {
     if (selectedDroneSound.type === "sampler" && sustainedSamplerRef.current && samplerLoaded) {
       // For sampler sounds, create looping system
       const loopingNode = createLoopingSamplerNote(midiNote);
       if (loopingNode) {
         loopingNode.startLooping();
         return { 
           type: "sampler_looping", 
           sampler: sustainedSamplerRef.current, 
           noteName: loopingNode.noteName,
           midiNote 
         };
       } else {
         // Fallback to oscillator
         return createOscillatorNode(frequency, audioContext);
       }
     } else {
       // For oscillator sounds or when sampler isn't ready
       return createOscillatorNode(frequency, audioContext);
     }
   };
 
   // Helper function to create oscillator nodes
   const createOscillatorNode = (frequency, audioContext) => {
     const oscillator = audioContext.createOscillator();
     const gainNode = audioContext.createGain();
     
     oscillator.type = selectedDroneSound.oscillatorType || 'sine';
     oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
     
     gainNode.gain.setValueAtTime(0, audioContext.currentTime);
     gainNode.gain.linearRampToValueAtTime(sustainedVolume * 0.15, audioContext.currentTime + 0.1);
     
     oscillator.connect(gainNode);
     gainNode.connect(audioContext.destination);
     
     return { 
       type: "oscillator", 
       oscillator, 
       gainNode 
     };
   };
 
   // Start sustained notes audio - updated to handle looping samplers
   const startSustainedNotes = async () => {
     if (sustainedNotes.length === 0) return;
     // Wait for a selected instrument's samples before starting so we don't
     // briefly play an oscillator fallback; the effect restarts us once
     // samplerLoaded flips true.
     if (selectedDroneSound.type === "sampler" && !samplerLoaded) {
       stopSustainedNotes();
       return;
     }
     const droneCtx = ensureDroneContext();

     stopSustainedNotes(); // Stop any existing notes
     
     for (const midiNote of sustainedNotes) {
       const frequency = getFrequencyForNote(midiNote);
       const audioNode = await createSustainedAudioNode(frequency, droneCtx, midiNote);
       
       if (audioNode.type === "oscillator") {
         audioNode.oscillator.start();
       }
       
       sustainedOscillatorsRef.current.push({
         ...audioNode,
         midiNote
       });
     }
   };
 
   // Stop sustained notes audio - updated to handle looping samplers
   const stopSustainedNotes = () => {
     console.log(`🛑 stopSustainedNotes called - samplerRef exists: ${!!sustainedSamplerRef.current}`);
     
     // Clear all looping intervals
     loopingIntervalsRef.current.forEach((intervalId, midiNote) => {
       clearInterval(intervalId);
     });
     loopingIntervalsRef.current.clear();
     
     // Stop all active sample instances (but DON'T dispose the sampler)
     activeSampleInstancesRef.current.forEach((instances, midiNote) => {
       instances.forEach(instance => {
         try {
           if (sustainedSamplerRef.current && instance.noteName) {
             sustainedSamplerRef.current.triggerRelease(instance.noteName, Tone.now() + 0.1);
           }
         } catch (e) {
           // Sample may already be stopped
         }
       });
     });
     activeSampleInstancesRef.current.clear();
     
     // Stop oscillators
     const droneCtx = droneCtxRef.current;
     sustainedOscillatorsRef.current.forEach((node) => {
       try {
         if (node.type === "oscillator" && droneCtx) {
           node.gainNode.gain.linearRampToValueAtTime(0, droneCtx.currentTime + 0.1);
           node.oscillator.stop(droneCtx.currentTime + 0.1);
           node.oscillator.disconnect();
           node.gainNode.disconnect();
         }
       } catch (e) {
         // Audio node may already be stopped/disconnected
       }
     });
     sustainedOscillatorsRef.current = [];
     
     // IMPORTANT: Don't dispose the sampler here - only stop playing notes
     // The sampler should only be disposed when changing instruments
     console.log(`✅ stopSustainedNotes completed - sampler still exists: ${!!sustainedSamplerRef.current}`);
   };
 
   // Real-time update of sustained notes during playback - updated for looping samplers
   const updateSustainedNotesRealTime = async () => {
     const droneCtx = ensureDroneContext();
     
     // Get currently playing notes
     const currentlyPlaying = sustainedOscillatorsRef.current.map(node => node.midiNote);
     
     // Find notes to start (in sustainedNotes but not currently playing)
     const notesToStart = sustainedNotes.filter(note => !currentlyPlaying.includes(note));
     
     // Find notes to stop (currently playing but not in sustainedNotes)
     const notesToStop = currentlyPlaying.filter(note => !sustainedNotes.includes(note));
     
     // Stop notes that should no longer be playing
     notesToStop.forEach(midiNote => {
       // Stop looping interval if it exists
       const intervalId = loopingIntervalsRef.current.get(midiNote);
       if (intervalId) {
         clearInterval(intervalId);
         loopingIntervalsRef.current.delete(midiNote);
       }
       
       // Stop active sample instances
       const instances = activeSampleInstancesRef.current.get(midiNote);
       if (instances) {
         instances.forEach(instance => {
           try {
             if (sustainedSamplerRef.current && instance.noteName) {
               sustainedSamplerRef.current.triggerRelease(instance.noteName, Tone.now() + 0.1);
             }
           } catch (e) {
             // Sample may already be stopped
           }
         });
         activeSampleInstancesRef.current.delete(midiNote);
       }
       
       // Remove from oscillators array
       const nodeIndex = sustainedOscillatorsRef.current.findIndex(node => node.midiNote === midiNote);
       if (nodeIndex >= 0) {
         const node = sustainedOscillatorsRef.current[nodeIndex];
         try {
           if (node.type === "oscillator") {
             node.gainNode.gain.linearRampToValueAtTime(0, droneCtx.currentTime + 0.1);
             node.oscillator.stop(droneCtx.currentTime + 0.1);
             node.oscillator.disconnect();
             node.gainNode.disconnect();
           }
         } catch (e) {
           // Audio node may already be stopped
         }
         sustainedOscillatorsRef.current.splice(nodeIndex, 1);
       }
     });
     
     // Start new notes
     for (const midiNote of notesToStart) {
       const frequency = getFrequencyForNote(midiNote);
       const audioNode = await createSustainedAudioNode(frequency, droneCtx, midiNote);
       
       if (audioNode.type === "oscillator") {
         audioNode.oscillator.start();
       }
       
       sustainedOscillatorsRef.current.push({
         ...audioNode,
         midiNote
       });
     }
   };
 
   // Sustained notes are a drone that plays on key-press, independent of the
   // metronome transport: a note sounds the moment it's selected and stops when
   // deselected — no need to press Start.
   useEffect(() => {
     const droneSig = `${selectedDroneSound.id}:${samplerLoaded}`;
     const soundChanged = droneSig !== lastDroneSigRef.current;
     lastDroneSigRef.current = droneSig;

     if (sustainedNotes.length === 0) {
       // Nothing selected — silence the drone.
       stopSustainedNotes();
     } else if (soundChanged || sustainedOscillatorsRef.current.length === 0) {
       // Sound (or its samples) changed, or starting fresh — rebuild the drone.
       startSustainedNotes();
     } else {
       // Same sound, selection changed — add/remove individual notes only.
       updateSustainedNotesRealTime();
     }
   }, [sustainedNotes, selectedDroneSound, samplerLoaded]);

   // Live volume: re-level a sounding drone as the slider is dragged, without
   // re-pressing the note. Oscillators ramp their own gain node; the shared
   // sampler updates its output volume.
   useEffect(() => {
     const ctx = droneCtxRef.current;
     if (ctx) {
       sustainedOscillatorsRef.current.forEach((node) => {
         if (node.type === "oscillator" && node.gainNode) {
           const g = node.gainNode.gain;
           g.cancelScheduledValues(ctx.currentTime);
           g.setValueAtTime(g.value, ctx.currentTime);
           g.linearRampToValueAtTime(sustainedVolume * 0.15, ctx.currentTime + 0.05);
         }
       });
     }
     if (sustainedSamplerRef.current) {
       sustainedSamplerRef.current.volume.value = Tone.gainToDb(sustainedVolume * 0.45);
     }
   }, [sustainedVolume]);

   // Live tuning: re-tune a sounding drone when the temperament or reference note
   // changes, without re-pressing. Oscillators glide smoothly to the new
   // frequency; the sampler (a single global detune) is rebuilt so each note
   // re-applies its offset on the next trigger.
   useEffect(() => {
     const ctx = droneCtxRef.current;
     if (!ctx || sustainedOscillatorsRef.current.length === 0) return;
     if (selectedDroneSound.type === "sampler") {
       startSustainedNotes();
     } else {
       sustainedOscillatorsRef.current.forEach((node) => {
         if (node.type === "oscillator" && node.oscillator) {
           node.oscillator.frequency.setTargetAtTime(
             getFrequencyForNote(node.midiNote), ctx.currentTime, 0.02
           );
         }
       });
     }
   }, [temperamentSystem, referenceNote]);

   // Cleanup on component unmount
   useEffect(() => {
     return () => {
       stopSustainedNotes();
       // Close the dedicated drone context so audio doesn't leak when leaving the tab.
       if (droneCtxRef.current && droneCtxRef.current.state !== 'closed') {
         droneCtxRef.current.close().catch(() => {});
         droneCtxRef.current = null;
       }
       // Mark sampler as not in use when component unmounts
       if (selectedDroneSound.type === "sampler") {
         markSamplerNotInUse(selectedDroneSound.instrument, 'sustainedNotes');
       }
     };
   }, []);

    // Piano keyboard component - simplified without quality color coding
    const SustainedNotesKeyboard = () => {
      // Get available notes for the current instrument
      const availableNotes = selectedDroneSound.type === "sampler" 
        ? getAvailableNotesForInstrument(selectedDroneSound.instrument)
        : []; // For oscillators, we'll show all notes
  
      // Get octave range for the current instrument  
      const octaveRange = selectedDroneSound.type === "sampler"
        ? getOctaveRangeForInstrument(selectedDroneSound.instrument)
        : { min: 3, max: 6 }; // Default range for oscillators
  
      // Function to check if a note is available
      const isNoteAvailable = (midiNote) => {
        if (selectedDroneSound.type === "oscillator") {
          return true; // All notes available for oscillators
        }
        return availableNotes.includes(midiNote);
      };
  
      // Dynamic octave range based on instrument
      const octaves = [];
      for (let i = octaveRange.min; i <= octaveRange.max; i++) {
        octaves.push(i);
      }
  
      return (
        <div className="w-full max-w-6xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-4 text-center">Sustained Notes</h3>
          
          {/* Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Volume Control */}
            <div>
              <label className="block text-sm font-medium mb-2">Volume</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={sustainedVolume}
                onChange={(e) => setSustainedVolume(Number(e.target.value))}
                className="w-full"
                style={{ accentColor: "#059dd9" }}
              />
              <div className="text-xs text-gray-500 text-center mt-1">
                {Math.round(sustainedVolume * 100)}%
              </div>
            </div>
  
            {/* Drone Sound Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Drone Sound</label>
              <select
                value={selectedDroneSound.id}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  // Find the selected sound across all categories
                  const chosen = SIMPLE_METRONOME_DRONE_SOUNDS.reduce((found, category) => {
                    const sound = category.sounds.find(s => s.id === selectedId);
                    return sound || found;
                  }, SIMPLE_METRONOME_DRONE_SOUNDS[0].sounds[0]);
                  
                  setSelectedDroneSound(chosen);
                  
                  // Clear notes that won't be available with new instrument
                  if (chosen.type === "sampler") {
                    const newAvailableNotes = getAvailableNotesForInstrument(chosen.instrument);
                    setSustainedNotes(prev => prev.filter(note => newAvailableNotes.includes(note)));
                  }
                }}
                className="w-full p-2 border rounded text-sm"
                disabled={isPlaying}
              >
                {SIMPLE_METRONOME_DRONE_SOUNDS.map((category) => (
                  <optgroup key={category.category} label={category.category}>
                    {category.sounds.map((sound) => (
                      <option key={sound.id} value={sound.id}>
                        {sound.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {selectedDroneSound.type === "sampler" && (
                <div className="text-xs mt-1">
                  {samplerLoaded ? (
                    <span className="text-green-600">✓ Samples loaded</span>
                  ) : (
                    <span className="text-orange-600">⏳ Loading samples...</span>
                  )}
                </div>
              )}
            </div>
  
            {/* Temperament System */}
            <div>
              <label className="block text-sm font-medium mb-2">Tuning System</label>
              <select
                value={temperamentSystem}
                onChange={(e) => setTemperamentSystem(e.target.value)}
                className="w-full p-2 border rounded text-sm"
              >
                {Object.keys(TEMPERAMENT_SYSTEMS).map((system) => (
                  <option key={system} value={system}>
                    {system}
                  </option>
                ))}
              </select>
            </div>
  
            {/* Reference Note (for temperaments that need it) */}
            {TEMPERAMENT_SYSTEMS[temperamentSystem].needsReference && (
              <div>
                <label className="block text-sm font-medium mb-2">Reference Note</label>
                <select
                  value={referenceNote}
                  onChange={(e) => setReferenceNote(Number(e.target.value))}
                  className="w-full p-2 border rounded text-sm"
                >
                  {NOTE_OPTIONS_12.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
  
          {/* Instrument Info */}
          {selectedDroneSound.type === "sampler" && (
            <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
              <div className="font-medium">Available Range: {selectedDroneSound.instrument}</div>
              <div className="text-gray-600 mt-1">
                {availableNotes.length} notes available (octaves {octaveRange.min}-{octaveRange.max})
              </div>
            </div>
          )}
  
          {/* Description */}
          <p className="text-xs text-gray-500 mb-4 text-center">
            {TEMPERAMENT_SYSTEMS[temperamentSystem].description}
          </p>
  
          {/* Piano Keyboard - Simplified */}
          <div className="border border-gray-300 rounded-md overflow-hidden shadow-sm bg-white">
            {octaves.map(octave => (
              <div key={octave} className="piano-row flex border-b border-gray-300 last:border-b-0">
                {/* Octave label */}
                <div className="w-8 flex items-center justify-center bg-gray-100 border-r border-gray-300 text-xs font-medium">
                  {octave}
                </div>
                
                {/* White keys row */}
                <div className="flex flex-1">
                  {["C", "D", "E", "F", "G", "A", "B"].map((note, noteIdx) => {
                    const semitoneMap = {C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11};
                    const semitone = semitoneMap[note];
                    const midiNote = (octave * 12 + semitone) + 12;
                    const isSelected = sustainedNotes.includes(midiNote);
                    const isAvailable = isNoteAvailable(midiNote);
                    const isEKey = note === "E";
                    
                    const getBackgroundColor = () => {
                      if (!isAvailable) return 'bg-gray-200 cursor-not-allowed';
                      if (isSelected) return 'bg-brand-500 bg-opacity-30 hover:bg-opacity-40';
                      return 'bg-white hover:bg-gray-50';
                    };
                    
                    return (
                      <div key={note} className="relative flex-1 h-8 sm:h-12 max-h-8 sm:max-h-12">
                        {/* White key */}
                        <div 
                          onClick={() => isAvailable && toggleSustainedNote(semitone, octave)}
                          className={`
                            absolute inset-0 
                            ${isEKey ? 'border-r-2 border-r-gray-400' : 'border-r border-gray-200'} 
                            last:border-r-0 border-t border-b
                            flex items-end justify-center pb-1
                            ${getBackgroundColor()}
                            transition-colors ${isAvailable ? 'cursor-pointer' : ''}
                          `}
                        >
                          <span className={`text-xs font-medium ${!isAvailable ? 'text-gray-400' : ''}`}>
                            {note}
                          </span>
                        </div>
                        
                        {/* Black keys */}
                        {(note === "C" || note === "D" || note === "F" || note === "G" || note === "A") && (
                          <SimplifiedBlackKey
                            octave={octave}
                            semitone={semitone + 1}
                            isPlaying={isPlaying}
                            toggleNote={toggleSustainedNote}
                            isSelected={sustainedNotes.includes((octave * 12 + semitone + 1) + 12)}
                            isAvailable={isNoteAvailable((octave * 12 + semitone + 1) + 12)}
                            isMobile={window.innerWidth < 640}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          {/* Selected notes display */}
          <div className="mt-4">
            <div className="text-sm font-medium mb-2">Selected Notes:</div>
            <div className="flex flex-wrap gap-2">
              {sustainedNotes.length > 0 ? (
                sustainedNotes.map(midiNote => {
                  const octave = Math.floor((midiNote - 12) / 12);
                  const semitone = (midiNote - 12) % 12;
                  const noteNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
                  const noteName = noteNames[semitone];
                  
                  return (
                    <div 
                      key={midiNote} 
                      className="px-3 py-1 border border-gray-400 bg-gray-50 rounded-full text-sm flex items-center"
                    >
                      <span>{noteName}{octave}</span>
                      <button 
                        onClick={() => toggleSustainedNote(semitone, octave)}
                        className="ml-2 text-accent-600 hover:text-accent-500 font-bold"
                      >
                        ×
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-gray-500 italic">No notes selected</div>
              )}
            </div>
          </div>
        </div>
      );
    };
  
    // Simplified BlackKey component without quality coloring
    const SimplifiedBlackKey = ({ octave, semitone, isPlaying, toggleNote, isSelected, isAvailable, isMobile }) => {
      const sharpNames = {
        1: "C#",
        3: "D#",
        6: "F#",
        8: "G#",
        10: "A#"
      };
      
      const noteName = sharpNames[semitone % 12];
      
      const getBackgroundColor = () => {
        if (!isAvailable) return 'bg-gray-500 cursor-not-allowed';
        if (isSelected) return 'bg-brand-700 hover:bg-brand-700';
        return 'bg-gray-800 hover:bg-gray-700';
      };
      
      return (
        <div 
          onClick={() => isAvailable && toggleNote(semitone, octave)}
          className={`
            absolute w-[60%] h-5 sm:h-7 -right-[30%] top-0 z-10
            ${getBackgroundColor()}
            flex items-center justify-center
            text-white text-[10px] font-medium
            ${isAvailable ? 'cursor-pointer' : ''} rounded-b-sm
            transition-colors
          `}
        >
          {noteName}
        </div>
      );
    };
    
  // Metronome Sound Settings component
  const MetronomeSoundSettings = () => {
    return (
      <div className="w-full max-w-6xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <h3 className="text-xl font-bold mb-4 text-center">Metronome Sounds</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {/* Volume Control */}
          <div>
            <label className="block text-sm font-medium mb-2">Click Volume</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={metronomeVolume}
              onChange={(e) => setMetronomeVolume(Number(e.target.value))}
              className="w-full mb-2"
              style={{ accentColor: "#059dd9" }}
            />
            <div className="text-xs text-gray-500 text-center">
              {Math.round(metronomeVolume * 100)}%
            </div>
          </div>

          {/* Downbeat Sound */}
          <div>
            <label className="block text-sm font-medium mb-2">Downbeat Sound</label>
            <select
              value={downbeatSound.id}
              onChange={(e) => {
                const selected = SOUND_OPTIONS.find(s => s.id === e.target.value) || SOUND_OPTIONS[0];
                setDownbeatSound(selected);
              }}
              className="w-full p-2 border rounded text-sm"
            >
              {SOUND_OPTIONS.map((sound) => (
                <option key={sound.id} value={sound.id}>
                  {sound.name}
                </option>
              ))}
            </select>
          </div>

          {/* Regular Beat Sound */}
          <div>
            <label className="block text-sm font-medium mb-2">Regular Beat Sound</label>
            <select
              value={beatSound.id}
              onChange={(e) => {
                const selected = SOUND_OPTIONS.find(s => s.id === e.target.value) || SOUND_OPTIONS[0];
                setBeatSound(selected);
              }}
              className="w-full p-2 border rounded text-sm"
            >
              {SOUND_OPTIONS.map((sound) => (
                <option key={sound.id} value={sound.id}>
                  {sound.name}
                </option>
              ))}
            </select>
          </div>
        </div>

      </div>
    );
  };

  // Function to toggle sustained notes
  const toggleSustainedNote = (semitone, octave) => {
    const midiNote = (octave * 12 + semitone) + 12;

    // Resume/create audio inside the click handler (a user gesture) so the drone
    // can sound immediately, without waiting for the metronome to start.
    ensureDroneContext();
    if (selectedDroneSound.type === "sampler" && Tone.context.state !== "running") {
      Tone.start().catch(() => {});
    }

    setSustainedNotes(prev => {
      if (prev.includes(midiNote)) {
        return prev.filter(note => note !== midiNote);
      } else {
        return [...prev, midiNote].sort((a, b) => a - b);
      }
    });
  };

  // Function to get frequency for a note based on temperament
  const getFrequencyForNote = (midiNote, aTuning = 440) => {
    if (temperamentSystem === "Equal Temperament") {
      return aTuning * Math.pow(2, (midiNote - 69) / 12);
    } else if (temperamentSystem === "Just Intonation") {
      const semitone = (midiNote - 12) % 12;
      const octave = Math.floor((midiNote - 12) / 12);
      
      if (semitone === referenceNote) {
        // This IS the reference note, use equal temperament
        return aTuning * Math.pow(2, (midiNote - 69) / 12);
      }
      
      const interval = (semitone - referenceNote + 12) % 12;
      const jiRatio = JI_SEMITONE_RATIOS[interval];
      
      if (jiRatio) {
        // Calculate the frequency of the reference note in the same octave
        const referenceNoteMidiNumber = (octave * 12 + referenceNote) + 12;
        const referenceFreq = aTuning * Math.pow(2, (referenceNoteMidiNumber - 69) / 12);
        return referenceFreq * (jiRatio.numerator / jiRatio.denominator);
      } else {
        // Fallback to equal temperament
        return aTuning * Math.pow(2, (midiNote - 69) / 12);
      }
    } else {
      // Historical temperaments
      const semitone = (midiNote - 12) % 12;
      const temperament = TEMPERAMENT_SYSTEMS[temperamentSystem];
      const etFreq = aTuning * Math.pow(2, (midiNote - 69) / 12);
      const offset = temperament.offsets[semitone];
      return etFreq * Math.pow(2, offset / 1200);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      {/* Large Beat Display - Always Centered */}
      <div className="w-full flex justify-center">
        <BeatDisplay />
      </div>

         {/* Start/Stop Button - Centered */}
         <div className="w-full flex justify-center">
        <button
          onClick={onStartStop}
          className={`
            px-12 py-6 text-2xl font-bold rounded-lg shadow-lg transition-all
            ${isPlaying 
              ? 'bg-red-500 hover:bg-red-600 text-white transform hover:scale-105' 
              : 'bg-brand-500 hover:bg-brand-700 text-white transform hover:scale-105'
            }
          `}
        >
          {isPlaying ? 'Stop' : 'Start'}
        </button>
      </div>
      
      {/* Controls Grid - Responsive and Centered */}
      <div className="w-full max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8 mb-8">
          
          {/* Tempo Control */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <label className="block text-lg font-medium mb-3">Tempo (BPM)</label>
            <input
              type="number"
              min="40"
              max="200"
              value={tempo}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  // Allow empty string temporarily while user is typing
                  setTempo("");
                } else {
                  // Convert to number, but ensure it's within valid range
                  const numericValue = Number(value);
                  if (!isNaN(numericValue)) {
                    setTempo(numericValue);
                  }
                }
              }}
              className="w-full p-3 border rounded text-center text-2xl font-bold mb-3"
              disabled={isPlaying}
            />
            <input
              type="range"
              min="40"
              max="200"
              value={tempo}
              onChange={(e) => setTempo(Number(e.target.value))}
              className="w-full"
              disabled={isPlaying}
              style={{ accentColor: "#059dd9" }}
            />
          </div>

                    {/* Meter */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
            <label className="block text-lg font-medium mb-3">Meter</label>
            <select
              value={`${selectedMeter.numerator}/${selectedMeter.denominator}`}
              onChange={(e) => {
                if (isPlaying) return;
                
                const [num, den] = e.target.value.split('/').map(Number);
                const meter = { numerator: num, denominator: den };
                setSelectedMeter(meter);
                
                console.log(`=== CHANGING METER TO ${num}/${den} ===`);
                
                // Create the exact number of beats needed
                const newBeatSounds = [];
                for (let i = 0; i < num; i++) {
                  const beat = createNewBeat();
                  beat.subdivisions = [{
                    subCount: subdivision,
                    accentedSubs: subdivision > 1 ? [1] : [],
                    mutedSubs: [],
                  }];
                  newBeatSounds.push(beat);
                }
                
                console.log(`Created ${newBeatSounds.length} beats for ${num}/${den}`);
                
                // DIRECTLY update the measures array instead of using updateMeasure
                setMeasures(prevMeasures => {
                  const newMeasures = [...prevMeasures];
                  newMeasures[0] = {
                    ...newMeasures[0],
                    numerator: num,
                    denominator: den,
                    beatSounds: newBeatSounds
                  };
                  
                  console.log(`Direct measure update - new measure:`, newMeasures[0]);
                  console.log(`New measure has ${newMeasures[0].beatSounds.length} beats`);
                  
                  return newMeasures;
                });
              }}
              className="w-full p-3 border rounded text-lg"
              disabled={isPlaying}
            >
              {PRESET_METERS.map((meter) => (
                <option key={meter.name} value={`${meter.numerator}/${meter.denominator}`}>
                  {meter.name}
                </option>
              ))}
            </select>
          </div>

          {/* Note Value */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <label className="block text-lg font-medium mb-3">Note Gets Beat</label>
            <select
              value={selectedNoteValue?.label || ""}
              onChange={(e) => {
                const newChoice = NOTE_VALUE_OPTIONS.find(
                  (opt) => opt.label === e.target.value
                );
                setSelectedNoteValue(newChoice);
              }}
              className="w-full p-3 border rounded text-lg"
              disabled={isPlaying}
            >
              {NOTE_VALUE_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.label}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Subdivision */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <label className="block text-lg font-medium mb-3">Subdivision</label>
            <select
              value={subdivision}
              onChange={(e) => {
                if (isPlaying) return; // Prevent changes while playing
                
                const newSubdivision = Number(e.target.value);
                setSubdivision(newSubdivision);
                updateAllBeatsSubdivision(newSubdivision);
              }}
              className="w-full p-3 border rounded text-lg"
              disabled={isPlaying}
            >
              {SUBDIVISION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sustained Notes Keyboard */}
      {/* Called as a function (not <SustainedNotesKeyboard />) so its DOM is
          inlined and persists across re-renders. As an element it remounted on
          every render — a new fn identity each time — which interrupted slider
          drags after a single step. */}
      {SustainedNotesKeyboard()}

      {/* Metronome Sound Settings */}
      {MetronomeSoundSettings()}
    </div>
  );
}

export { SimpleMetronome };